/**
 * reminderNotifications.js
 *
 * Schedules local notifications for Notes & Reminders.
 *
 * The stored reminders are the source of truth; the OS queue is a cache we
 * rebuild. That is what makes this safe to call as often as we like - on save,
 * on delete, on tick, and on app open.
 */
import { LocalNotifications } from '@capacitor/local-notifications';

// iOS keeps at most 64 pending notifications per app and silently drops the
// rest, so never hand it more than this. The rest get scheduled on a later
// pass as the near ones fire.
const MAX_SCHEDULED = 50;

// Tag on everything we create, so cancelling can never touch a notification
// scheduled by some other part of the app.
const KIND = 'worthly-reminder';

/** Stable 31-bit integer from a string id - the plugin will not take strings. */
export function notifIdFor(id) {
  let h = 5381;
  for (let i = 0; i < String(id).length; i += 1) {
    h = ((h * 33) ^ String(id).charCodeAt(i)) >>> 0;
  }
  // Keep it comfortably inside a signed 32-bit int.
  return h % 2000000000;
}

/** 'YYYY-MM-DDTHH:mm' in LOCAL time, which is how reminders are stored. */
function toLocalDate(dueAt) {
  if (!dueAt) return null;
  const [d, t] = String(dueAt).split('T');
  const [y, m, day] = d.split('-').map(Number);
  const [hh, mm] = (t || '12:00').split(':').map(Number);
  if (!y || !m || !day) return null;
  return new Date(y, m - 1, day, hh || 0, mm || 0, 0, 0);
}

export async function permissionState() {
  try {
    const res = await LocalNotifications.checkPermissions();
    return res.display;
  } catch (e) {
    return 'denied';
  }
}

/**
 * Granted already? Do nothing. Never asked? Ask once. Refused? Leave it —
 * pestering someone who said no is how an app gets its notifications muted
 * entirely.
 */
export async function ensurePermission() {
  const state = await permissionState();
  if (state === 'granted') return true;
  if (state === 'denied') return false;
  try {
    const res = await LocalNotifications.requestPermissions();
    return res.display === 'granted';
  } catch (e) {
    return false;
  }
}

async function cancelOurs() {
  try {
    const pending = await LocalNotifications.getPending();
    const ours = (pending.notifications || [])
      .filter((n) => n.extra && n.extra.kind === KIND)
      .map((n) => ({ id: n.id }));
    if (ours.length) await LocalNotifications.cancel({ notifications: ours });
  } catch (e) {
    /* nothing pending, or the plugin is unavailable on this platform */
  }
}

/**
 * Rebuild the OS queue from the given reminders.
 *
 * Only future, undone reminders are scheduled. Past ones are deliberately
 * skipped - the red Overdue group in the app is what carries those, and firing
 * a notification for something that was due last week helps nobody.
 */
export async function syncReminderNotifications(reminders) {
  const granted = await permissionState();
  if (granted !== 'granted') {
    // No permission means no queue to keep tidy either.
    await cancelOurs();
    return { scheduled: 0, permitted: false };
  }

  await cancelOurs();

  const now = Date.now();
  const upcoming = (reminders || [])
    .filter((r) => r && r.dueAt && !r.done)
    .map((r) => ({ r, at: toLocalDate(r.dueAt) }))
    .filter((x) => x.at && x.at.getTime() > now)
    .sort((a, b) => a.at - b.at)
    .slice(0, MAX_SCHEDULED);

  if (upcoming.length === 0) return { scheduled: 0, permitted: true };

  const notifications = upcoming.map(({ r, at }) => ({
    id: notifIdFor(r.id),
    title: '\u{1F6A8} Reminder',
    body: r.title || 'You have a reminder due',
    schedule: { at, allowWhileIdle: true },
    extra: { kind: KIND, reminderId: r.id },
  }));

  try {
    await LocalNotifications.schedule({ notifications });
    return { scheduled: notifications.length, permitted: true };
  } catch (e) {
    return { scheduled: 0, permitted: true, error: String(e) };
  }
}

/** Drop everything we scheduled - for switching reminders off entirely. */
export async function clearReminderNotifications() {
  await cancelOurs();
}

/** How many of ours are currently queued with the OS. Useful for debugging. */
export async function pendingCount() {
  try {
    const pending = await LocalNotifications.getPending();
    return (pending.notifications || []).filter((n) => n.extra && n.extra.kind === KIND).length;
  } catch (e) {
    return 0;
  }
}

/** Local 'YYYY-MM-DDTHH:mm' - deliberately not toISOString, which is UTC. */
function localStamp(d) {
  const p = (n) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate())
    + 'T' + p(d.getHours()) + ':' + p(d.getMinutes());
}

/**
 * How many reminders are overdue and unticked.
 *
 * Overdue means a whole day has passed, not merely the hour - something due at
 * 9am today is not overdue at 2pm. Shared so the badge, the list and the home
 * card cannot disagree about what the number means.
 */
export function countOverdue(reminders) {
  const now = localStamp(new Date());
  const today = now.slice(0, 10);
  return (reminders || []).filter((r) => r && r.dueAt && !r.done
    && r.dueAt < now && r.dueAt.slice(0, 10) !== today).length;
}

/**
 * Everything wanting attention right now: overdue, PLUS anything due later
 * today. Counted from midnight rather than from the due time on purpose - a
 * reminder that only surfaces once it is late is not reminding anyone of
 * anything. Sorted earliest first, and shared so the app icon badge and the
 * home banner can never disagree about the number.
 */
export function dueNow(reminders) {
  const today = localStamp(new Date()).slice(0, 10);
  return (reminders || [])
    .filter((r) => r && r.dueAt && !r.done && r.dueAt.slice(0, 10) <= today)
    .sort((a, b) => (a.dueAt < b.dueAt ? -1 : 1));
}

/** How many reminders are overdue or due today. */
export function countDueNow(reminders) {
  return dueNow(reminders).length;
}
